/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from "@mui/material";
import RequireRole from "../components/RequireRole";

const statusColors = {
  unprocessed: "default",
  processing: "info",
  done: "success",
  rejected: "error",
  invalid: "error",
};


function InsightCard({ insight, onDelete }) {
  const navigate = useNavigate();

  const intent =
  insight?.structured_data?.intent ||
  insight?.parsed_data?.intent ||
  null;

  const handleDelete = async () => {
    const confirmed = window.confirm("Delete this voice insight and re-record?");
    if (!confirmed) return;

    try {
      await api.delete(`/api/secure/my-insights/${insight.insight_uuid}`);
      onDelete(insight.id);
      navigate("/secure/voice"); // redirect to record again
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert("Error deleting. Try again.");
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={1}
    >
      <Typography variant="body2" color="text.secondary">
        {new Date(insight.created_at).toLocaleString()}
      </Typography>

      <Box display="flex" gap={1} alignItems="center">
        {intent && (
          <Chip
            size="small"
            variant="outlined"
            label={intent.replace(/_/g, " ")}
          />
        )}
        <Chip
          size="small"
          label={insight.status || "unprocessed"}
          color={statusColors[insight.status] || "default"}
        />
      </Box>
    </Box>

      <Box mb={2}>
        <audio controls src={insight.voice_url} style={{ width: "100%" }} />
      </Box>

      {/* Display structured/parsing results */}
      {insight.parsed_data && Object.keys(insight.structured_data || {}).length > 0 ? (
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            p: 2,
            borderRadius: 1,
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            overflowX: "auto",
          }}
        >
          <pre>{JSON.stringify(insight.parsed_data, null, 2)}</pre>
        </Box>
      ) : insight.status === "processing" ? (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          ⏳ AI is still analyzing this voice note...
        </Typography>
      ) : insight.status === "invalid" ? (
        <Typography variant="body2" color="error" fontStyle="italic">
          ⚠️ Could not process this voice note. Please re‑record.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No structured data yet.
        </Typography>
      )}

      {/* Delete button when insight is no longer pending */}
      {["done", "invalid", "error", "processing"].includes(insight.status) && (
        <Button
          onClick={handleDelete}
          variant="outlined"
          color="error"
          size="small"
          sx={{ mt: 2 }}
        >
          🗑 Delete & Re‑record
        </Button>
      )}
      {["done", "invalid", "error", "processing"].includes(insight.status) && (
      <Button
        variant="outlined"
        component={Link}
        to={`/secure/review-insights/${insight.id}`}
        size="small"
        sx={{ mt: 2 }}
      >
        ✏️ Review & Edit
      </Button>
      )} 
    </Paper>
  );
}

export default function MyInsightsPage() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    try {
      const res = await api.get("/api/secure/my-insights");
      setInsights(res.data);
    } catch (err) {
      console.error("❌ Failed to load insights:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
    const interval = setInterval(fetchInsights, 20000);
    return () => clearInterval(interval);
  }, []);

  return (
    <RequireRole roles={["field_agent", "reviewer", "admin"]}>
      <Box maxWidth="md" mx="auto" px={2} py={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          My Voice Insights
        </Typography>

        <Button
          component={Link}
          to="/secure/voice"
          variant="contained"
          sx={{ mb: 3 }}
        >
          🎙 Record New Voice Note
        </Button>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : insights.length === 0 ? (
          <Typography color="text.secondary">
            No insights yet — record a voice note to get started.
          </Typography>
        ) : (
          insights.map((insight) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onDelete={(id) =>
                setInsights((prev) => prev.filter((i) => i.id !== id))
              }
            />
          ))
        )}
      </Box>
    </RequireRole>
  );
}